import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Moon, Sparkles, Play, Loader2, RotateCcw, History, ChevronRight } from 'lucide-react';
import { useAuth } from '@/components/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MysteriaeDrawPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [spreads, setSpreads] = useState([]);
  const [selectedSpread, setSelectedSpread] = useState(null);
  const [question, setQuestion] = useState('');
  const [reversedEnabled, setReversedEnabled] = useState(false);
  const [includeMinor, setIncludeMinor] = useState(true);
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);
  const [drawResult, setDrawResult] = useState(null);
  const [revealedCards, setRevealedCards] = useState([]);
  const [showInterpretation, setShowInterpretation] = useState(false);

  useEffect(() => {
    fetchSpreads();
  }, []);

  const fetchSpreads = async () => {
    try {
      const response = await fetch(`${API_URL}/api/mysteriae/spreads`);
      const data = await response.json();
      setSpreads(data.spreads || []);
    } catch (error) {
      console.error('Error fetching spreads:', error);
      toast.error('Erreur lors du chargement des tirages');
    } finally {
      setLoading(false);
    }
  };

  const performDraw = async () => {
    if (!selectedSpread) {
      toast.error('Veuillez sélectionner un type de tirage');
      return;
    }

    setDrawing(true);
    setRevealedCards([]);
    setShowInterpretation(false);

    try {
      const response = await fetch(`${API_URL}/api/mysteriae/draw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          spread_id: selectedSpread.id,
          question: question || null,
          user_id: user?.user_id || null,
          reversed_enabled: reversedEnabled,
          include_minor: includeMinor
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors du tirage');
      }

      const data = await response.json();
      setDrawResult(data);

      // Reveal cards one by one with animation
      for (let i = 0; i < data.cards.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 800));
        setRevealedCards(prev => [...prev, i]);
      }

      // Show interpretation after all cards revealed
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowInterpretation(true);

    } catch (error) {
      console.error('Error performing draw:', error);
      toast.error('Erreur lors du tirage');
    } finally {
      setDrawing(false);
    }
  };

  const resetDraw = () => {
    setDrawResult(null);
    setRevealedCards([]);
    setShowInterpretation(false);
    setQuestion('');
  };

  const getSpreadIcon = (spreadId) => {
    switch (spreadId) {
      case 'three_cards': return '🎴';
      case 'situation': return '🎯';
      case 'celtic_cross': return '✝️';
      case 'yes_no': return '❓';
      case 'love': return '💕';
      default: return '🌟';
    }
  };

  const CardSlot = ({ card, index, small = false, className = '' }) => {
    const isRevealed = revealedCards.includes(index);
    const cardData = card?.card;

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: index * 0.1 }}
        className={`relative ${small ? 'w-20 sm:w-24' : 'w-28 sm:w-36'} ${className}`}
      >
        <div 
          className={`aspect-[2/3] rounded-xl overflow-hidden border-2 transition-all duration-500 ${
            isRevealed ? 'border-purple-400' : 'border-purple-200'
          } shadow-lg`}
        >
          <AnimatePresence mode="wait">
            {!isRevealed ? (
              <motion.div
                key="back"
                initial={{ rotateY: 0 }}
                exit={{ rotateY: 90 }}
                transition={{ duration: 0.3 }}
                className="w-full h-full bg-gradient-to-br from-purple-600 to-indigo-700 flex items-center justify-center"
              >
                <Moon className={`${small ? 'h-6 w-6' : 'h-10 w-10'} text-purple-300`} />
              </motion.div>
            ) : (
              <motion.div
                key="front"
                initial={{ rotateY: -90 }}
                animate={{ rotateY: 0 }}
                transition={{ duration: 0.3 }}
                className="w-full h-full relative bg-white"
              >
                <img
                  src={cardData?.image_url}
                  alt={cardData?.name}
                  className={`w-full h-full object-cover ${card.is_reversed ? 'rotate-180' : ''}`}
                />
                {card.is_reversed && (
                  <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-rose-500 rounded text-[10px] text-white font-bold">
                    R
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Position label */}
        <div className={`mt-2 text-center ${small ? 'text-xs' : 'text-sm'}`}>
          <p className="text-purple-700 font-medium truncate">{card?.position_name}</p>
          {isRevealed && cardData && (
            <p className="text-gray-700 truncate mt-0.5 font-semibold">{cardData.name}</p>
          )}
        </div>
      </motion.div>
    );
  };

  const renderCardGrid = () => {
    if (!drawResult) return null;

    // Default horizontal layout
    return (
      <div className={`flex flex-wrap justify-center gap-4 ${drawResult.cards.length > 3 ? 'max-w-4xl' : 'max-w-2xl'} mx-auto`}>
        {drawResult.cards.map((drawnCard, index) => (
          <CardSlot key={index} card={drawnCard} index={index} small={drawResult.cards.length > 5} />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-purple-50">
      {/* Decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-64 h-64 bg-purple-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-indigo-200/20 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-purple-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/mysteriae" 
            className="flex items-center gap-2 text-purple-600 hover:text-purple-700 transition-colors"
            data-testid="back-to-mysteriae"
          >
            <ChevronLeft className="h-5 w-5" />
            <span className="font-semibold">MYSTERIAE</span>
          </Link>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            <span className="font-bold text-gray-900 tracking-wide">Tirages</span>
          </div>
          {isAuthenticated && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/mysteriae/history')}
              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
              data-testid="history-btn"
            >
              <History className="h-4 w-4 mr-2" />
              Historique
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {!drawResult ? (
          /* Selection Phase */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                Choisissez votre tirage
              </h1>
              <p className="text-gray-600 leading-relaxed">
                Sélectionnez un type de tirage et posez votre question
              </p>
            </div>

            {/* Spread Selection */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <div key={i} className="h-32 bg-purple-100 rounded-xl animate-pulse" />
                ))
              ) : (
                spreads.map(spread => (
                  <button
                    key={spread.id}
                    onClick={() => setSelectedSpread(spread)}
                    className={`p-5 rounded-xl border-2 text-left transition-all ${
                      selectedSpread?.id === spread.id
                        ? 'bg-purple-50 border-purple-500 shadow-lg shadow-purple-100'
                        : 'bg-white border-purple-200 hover:border-purple-300 hover:shadow-md'
                    }`}
                    data-testid={`spread-${spread.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{getSpreadIcon(spread.id)}</span>
                      <div>
                        <h3 className="text-gray-900 font-semibold">{spread.name}</h3>
                        <p className="text-gray-600 text-sm mt-1 leading-relaxed">{spread.description}</p>
                        <p className="text-purple-600 text-xs mt-2 font-medium">
                          {spread.card_count} carte{spread.card_count > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Question Input */}
            {selectedSpread && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-xl mx-auto mb-8"
              >
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Votre question (optionnel)
                </label>
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Posez votre question ici..."
                  className="w-full p-4 bg-white border-2 border-purple-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 resize-none"
                  rows={3}
                  data-testid="question-input"
                />

                {/* Options */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-4">
                  <label className="flex items-center gap-2 text-gray-600 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeMinor}
                      onChange={(e) => setIncludeMinor(e.target.checked)}
                      className="rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                    />
                    Inclure les Arcanes Mineurs (78 cartes)
                  </label>
                  <label className="flex items-center gap-2 text-gray-600 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reversedEnabled}
                      onChange={(e) => setReversedEnabled(e.target.checked)}
                      className="rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                    />
                    Activer les cartes renversées
                  </label>
                </div>
              </motion.div>
            )}

            {/* Draw Button */}
            <div className="text-center">
              <Button
                size="lg"
                onClick={performDraw}
                disabled={!selectedSpread || drawing}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl px-12 shadow-lg disabled:opacity-50"
                data-testid="draw-btn"
              >
                {drawing ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Tirage en cours...
                  </>
                ) : (
                  <>
                    <Play className="h-5 w-5 mr-2" />
                    Commencer le tirage
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        ) : (
          /* Result Phase */
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {selectedSpread?.name}
              </h2>
              {question && (
                <p className="text-purple-600 italic font-medium">"{question}"</p>
              )}
            </div>

            {/* Cards Display */}
            {renderCardGrid()}

            {/* Interpretation */}
            <AnimatePresence>
              {showInterpretation && drawResult.interpretation && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mt-12 max-w-3xl mx-auto"
                >
                  <div className="p-6 bg-white rounded-2xl border-2 border-purple-200 shadow-lg">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                        <Sparkles className="h-4 w-4 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Interprétation</h3>
                    </div>
                    <div className="prose prose-gray max-w-none">
                      <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {drawResult.interpretation}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Button
                variant="outline"
                onClick={resetDraw}
                className="border-purple-200 text-purple-700 hover:bg-purple-50 rounded-xl"
                data-testid="new-draw-btn"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Nouveau tirage
              </Button>
              {revealedCards.length === drawResult.cards.length && (
                <Button
                  onClick={() => navigate('/mysteriae/arcanes')}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl"
                >
                  Explorer les Arcanes
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default MysteriaeDrawPage;
